import React from "react"
import {
    TextField,
    Button,
    Container,
    Typography,
    Box,
    Grid,
    CircularProgress,
    Alert,
} from "@mui/material"
import JSEncrypt from "jsencrypt"
import sha256 from "js-sha256"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { AuthenticationApi } from "../../../API/Authentication/LoginApi"

const Login: React.FC = () => {
    const [username, setUsername] = useState<string>("")
    const [password, setPassword] = useState<string>("")
    const [loginError, setLoginError] = useState<string | null>(null)
    const [loader, setLoader] = useState<boolean>(false)
    const navigate = useNavigate()

    const hexToBytes = (hex: string) => {
        const bytes: number[] = []
        for (let i = 0; i < hex.length; i += 2) {
            bytes.push(parseInt(hex.substr(i, 2), 16))
        }
        return bytes
    }

    const handleLogin = async () => {
        setLoader(true)
        setLoginError(null) 
        try {
            const timestamp = String(Date.now())
            const hash = sha256.sha256(password + timestamp)
            const hashBytes = hexToBytes(hash)
            const hashBase64 = btoa(String.fromCharCode(...hashBytes))
            const data = `${password}:${timestamp}:${hashBase64}`
            const encrypt = new JSEncrypt()
            encrypt.setPublicKey(
                "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAg79k2m8PlGNIyJum2uzGqnhdjNqc90SF5FwoOlU7I6Hy6xh36K2qzw1Q5wLcfM+KojFy0xLzKf/u03Wm9S/SmbwvzlXaCq5uYEtVqJBxCz3Y+ZyTRlWyWDJki+OvGYqGbw1EmmKFt2SaOjsE/e+lo6KQpFZwGuaJ1ZKQjCbKRdmkOUAWIjK51a3r8kTnq7XIMf9iXaHX5qvTS/BMcRrXTQ5/JGeuy9OCaJ0S7YrSINZymd0puA0IH87QxDmnUoq+pJwo8l67MFwK7rLyCDSRGwdVY4kLQw8T25k53eoH6+ZUMLydkR4X/ryXYERIw/OwPWpoioHARKzyA/ARA2jkTQIDAQAB"
            )
            const encryptedData = encrypt.encrypt(data)
            if (!encryptedData)
                throw new Error("Encryption failed. Invalid data.")
            const response =
                await AuthenticationApi.api.onlineUserAuthentication(
                    username,
                    encryptedData
                )
            setLoader(false)
            if (response.code === 2000 && response.data?.token) {
                localStorage.setItem("token", response.data.token);
                
                const authority = response.data.userDetail.authority[0]?.authority || "ADMIN";
                localStorage.setItem("authority", authority);
                localStorage.setItem("name", response.data.userDetail.name);
                // Navigate based on role
                if (authority === "ROLE_OPS_USER" || authority === "ROLE_CITY_HEAD") {
                  navigate("/client-ticket");
                }
                else if (authority === "ROLE_KYC") {
                    navigate("/kyc-ticket");
                }
                else {
                  navigate("/dashboard-selection");
                }
              } else {
                setLoginError(response.message || "Access Denied");
              }
              
        } catch (error) {
            console.error("Login error:", error)
            setLoader(false)
            setLoginError("Invalid Credentials or Network Error")
        }
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        handleLogin()
    }

    return (
        <form onSubmit={handleSubmit}>
            <Container maxWidth={false} disableGutters>
                <Grid container spacing={0} style={{ minHeight: "100vh" }}>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        style={{ padding: 0, height: "100vh" }}
                    >
                        <Box
                            component="img"
                            src="/assets/Dashboard-bro.svg"
                            alt="Login Illustration"
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={6}
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: 20,
                            position: "relative",
                        }}
                    >
                        <Box
                            component="img"
                            src="/assets/logo.svg"
                            alt="Logo"
                            sx={{
                                position: "fixed",
                                top: 20,
                                left: 20,
                                width: "80px",
                            }}
                        />
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            width="100%"
                            maxWidth={400}
                        >
                            <Typography
                                variant="h4"
                                component="h1"
                                gutterBottom
                            >
                                Login
                            </Typography>

                            <TextField
                                label="Username"
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                required
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                            <TextField
                                label="Password"
                                type="password"
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                required
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value)
                                }}
                            />
                            {loader ? (
                                <CircularProgress style={{ marginTop: "16px" }} />
                            ) : (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    style={{
                                        marginTop: "16px",
                                        backgroundColor: "#1FCD5B",
                                    }}
                                >
                                    Login
                                </Button>
                            )}
                            {loginError && (
                                <Alert severity="error" style={{ marginTop: "16px" }}>
                                    {loginError}
                                </Alert>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </form>
    )
}

export default Login